import { useDocument } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools";
import type { TransferRequest } from "@/utils/types";
import type { Account } from "@aircap/common/account";
import type { SwanTransferRequest } from "@aircap/common/swan";
import {
  treezorTransferRequestFullPath,
  type TransferRequest as TreezorTransferRequest,
} from "@aircap/common/treezor";
import type { Timestamp } from "@firebase/firestore-types";
import { collection, doc, updateDoc, type WithFieldValue } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { useAccountCurrency } from "./useAccountCurrency";

export type FormattedTransfer = TransferRequest & {
  id: string;
  translatedStatus: { title: string; tooltip: string | null };
  color: string;
  formattedAmount: string;
  formattedDate: string;
  /** Swan only. Absent until the payout transaction exists. */
  linkedTransactionStatus?:
    | "Booked"
    | "Deferred"
    | "Rejected"
    | "Pending"
    | "Canceled"
    | "Upcoming"
    | "Released"
    | "Settled";
};

export const useTransferRequest = ({
  accountId,
  transferRequestId,
}: {
  accountId: Ref<string>;
  transferRequestId?: Ref<string | null>;
}) => {
  const { t, locale } = useI18n();
  const accountSource = computed(() => doc(collection<Account>(db, "accounts"), accountId.value));
  const { data: account } = useDocument<Account>(accountSource);
  const { currencyCode } = useAccountCurrency(accountId);

  const transferRequestRef = computed(() => {
    if (!transferRequestId?.value) return undefined;
    switch (account.value?.accountType) {
      case "swan":
        return doc<SwanTransferRequest>(
          db,
          `accounts/${accountId.value}/swanTransferRequests/${transferRequestId.value}`,
        );
      case "treezor":
        return doc<TreezorTransferRequest>(
          db,
          treezorTransferRequestFullPath(accountId.value, transferRequestId.value),
        );
      case "bridge":
      case "gcl":
      case "powens":
      case undefined:
        return undefined;
    }
  });
  const { data: rawTransferRequest } = useDocument<SwanTransferRequest | TreezorTransferRequest>(
    () => transferRequestRef.value,
  );
  const transferRequest = computed(() => {
    if (!rawTransferRequest.value) return undefined;
    return formatTransferRequest(rawTransferRequest.value);
  });

  const updateTransferRequest = async (
    transferRequest: WithFieldValue<Pick<TransferRequest, "trackedCategoryId">>,
  ) => {
    if (!transferRequestRef.value) return;
    await updateDoc(transferRequestRef.value, transferRequest);
  };

  const translateStatus = (
    status: TransferRequest["status"],
  ): { title: string; tooltip: string | null } => {
    switch (status) {
      case "pending":
        return {
          title: t("transfers.status.pending.title"),
          tooltip: t("transfers.status.pending.tooltip"),
        };
      case "completed":
        return {
          title: t("transfers.status.completed.title"),
          tooltip: t("transfers.status.completed.tooltip"),
        };
      case "accepted":
        return {
          title: t("transfers.status.accepted.title"),
          tooltip: t("transfers.status.accepted.tooltip"),
        };
      case "rejected":
        return {
          title: t("transfers.status.rejected.title"),
          tooltip: t("transfers.status.rejected.tooltip"),
        };
      case "cancelled":
        return {
          title: t("transfers.status.cancelled.title"),
          tooltip: t("transfers.status.cancelled.tooltip"),
        };
    }
  };

  const statusColor = (status: TransferRequest["status"]): string => {
    switch (status) {
      case "pending":
        return "warning";
      case "completed":
        return "success";
      case "accepted":
        return "primary";
      case "rejected":
        return "error";
      case "cancelled":
        return "#9E9E9E";
    }
  };

  const formatDate = (date: Timestamp | undefined): string => {
    if (!date) return "";
    return new Date(date.toDate()).toLocaleDateString(locale.value);
  };

  const formatTransferRequest = (
    transfer: (SwanTransferRequest | TreezorTransferRequest) & { id: string },
  ): FormattedTransfer => {
    return {
      amount: transfer.amount,
      beneficiaryName: transfer.beneficiaryName,
      beneficiaryIban: transfer.beneficiaryIban,
      createdAt: transfer.createdAt,
      status: transfer.status,
      statusUpdatedAt: transfer.statusUpdatedAt,
      editedAt: "editedAt" in transfer ? transfer.editedAt : undefined,
      transactionId:
        "swan_transaction_id" in transfer
          ? transfer.swan_transaction_id
          : "treezorOperationId" in transfer
            ? transfer.treezorOperationId
            : undefined,
      trackedCategoryId: transfer.trackedCategoryId,
      decisions: transfer.decisions,
      verificationOfPayee: transfer.verificationOfPayee,
      files: transfer.files,
      label: transfer.label,
      reference: transfer.reference,
      id: transfer.id, // Explicitly preserve id (VueFire adds it but spread might not copy it)
      ...("swan_transaction_status" in transfer
        ? { linkedTransactionStatus: transfer.swan_transaction_status }
        : {}),
      translatedStatus: translateStatus(transfer.status),
      color: statusColor(transfer.status),
      formattedAmount: currencyCode.value
        ? formatCurrency({
            value: -parseFloat(transfer.amount),
            currencyCode: currencyCode.value,
            sign: true,
            locale: locale.value,
          })
        : "",
      formattedDate: formatDate(transfer.createdAt),
    };
  };

  return { formatTransferRequest, transferRequest, updateTransferRequest };
};
